/*  ========================================================================
    # Sections - Card
    ========================================================================  */
import { AnimatePresence, MotionConfig, motion } from 'motion/react';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import useTranslation from '../../hooks/useTranslation';
import useSVGSprite from '../../hooks/useSVGSprite';

const Card = ({ contentLeft, contentRight, cards }) => {
  const __ = useTranslation();
  const sprite = useSVGSprite();
  const wrapperRef = useRef(null);
  const indicatorRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const slider = wrapperRef.current;
    if (!slider) return;

    const updateProgress = () => {
      const maxScroll = slider.scrollWidth - slider.clientWidth;
      const progress = maxScroll > 0 ? slider.scrollLeft / maxScroll : 0;
      setScrollProgress(progress);
      if (indicatorRef.current) {
        indicatorRef.current.style.setProperty('--scroll-progress', progress);
      }
    };

    updateProgress();
    slider.addEventListener('scroll', updateProgress, { passive: true });
    return () => slider.removeEventListener('scroll', updateProgress);
  }, [cards]);

  const scrollToCard = direction => {
    const slider = wrapperRef.current;
    if (!slider) return;

    const cardEl = slider.querySelector('.s-cards__card');
    if (!cardEl) return;

    const cardWidth = cardEl.offsetWidth;
    slider.scrollBy({ left: direction * cardWidth, behavior: 'smooth' });
  };

  return (
    <div className="container">
      <div className="s-cards__wrapper">
        {contentLeft && (
          <div className="s-cards__content-left">
            <div className="wysiwyg-block" dangerouslySetInnerHTML={{ __html: contentLeft }}></div>
          </div>
        )}
        {contentRight && (
          <div className="s-cards__content-right">
            <div className="wysiwyg-block" dangerouslySetInnerHTML={{ __html: contentRight }}></div>
          </div>
        )}
        <MotionConfig reducedMotion="user">
          <div className={`s-cards__slider`} id="cardlist" ref={wrapperRef}>
            <AnimatePresence mode="popLayout">
              {cards.map((card, index) => {
                const Component = card.link ? motion.a : motion.div;
                return (
                  <Component
                    key={index}
                    className="s-cards__card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    {...(card.link && {
                      href: card.link.url,
                      target: card.link.target,
                      rel: card.link.target === '_blank' ? 'noopener noreferrer' : undefined
                    })}
                  >
                    {card.image && (
                      <figure className="s-cards__card-image">
                        <img src={card.image.sizes.large} alt={card.image.alt} />
                      </figure>
                    )}
                    <div className="s-cards__card-content">
                      <div
                        className="wysiwyg-block"
                        dangerouslySetInnerHTML={{ __html: card.content }}
                      ></div>
                      {card.link && (
                        <span className="s-cards__card-link">
                          {card.link.title}
                        </span>
                      )}
                    </div>
                  </Component>
                );
              })}
            </AnimatePresence>
          </div>
          <div className="s-cards__scroller">
            <div className="s-cards__indicator" ref={indicatorRef}></div>
            <div className="s-cards__controls">
              <button
                type="button"
                className="s-cards__control s-cards__control--prev"
                disabled={scrollProgress <= 0}
                onClick={() => scrollToCard(-1)}
              >
                <svg viewBox="0 0 50 24">
                  <title>Arrow left</title>
                  <use href={`${sprite}#arrow-left`} />
                </svg>
              </button>
              <button
                type="button"
                className="s-cards__control s-cards__control--next"
                disabled={scrollProgress >= 1}
                onClick={() => scrollToCard(1)}
              >
                <svg viewBox="0 0 50 24">
                  <title>Arrow right</title>
                  <use href={`${sprite}#arrow-right`} />
                </svg>
              </button>
            </div>
          </div>
        </MotionConfig>
      </div>
    </div>
  );
};

Card.propTypes = {
  contentLeft: PropTypes.string,
  contentRight: PropTypes.string,
  cards: PropTypes.array
};

export default Card;
